/** Max decimal places for multiplier in the OKR KR weighting wizard. */
export const KR_WEIGHT_MULTIPLIER_INPUT_DECIMAL_PLACES = 3;

/** Metric progresses the objective weighted roll-up when {@link Metric.progress_consider} resolves to this. */
export const KR_METRIC_COUNTS_TOWARD_WEIGHTED_PROGRESS = 1;

/**
 * Matches UI rows that contribute to KR share (%) totals. Numeric compare so `"1"` and `1` both count.
 *
 * @param {{ progress_consider?: unknown }} metric
 * @returns {boolean}
 */
export function isMetricCountedForKrWeighting(metric) {
  return (
    Number(metric && metric.progress_consider) ===
    KR_METRIC_COUNTS_TOWARD_WEIGHTED_PROGRESS
  );
}

/**
 * KR share (%) label: up to {@link KR_WEIGHT_MULTIPLIER_INPUT_DECIMAL_PLACES} decimals, trailing zeros trimmed (matches PHP weightage_display).
 *
 * @param {unknown} sharePercent rounded share 0–100
 * @returns {string}
 */
export function formatKrSharePercentDisplay(sharePercent) {
  const x = Number(sharePercent);
  if (!Number.isFinite(x)) {
    return '0';
  }
  let s = x.toFixed(KR_WEIGHT_MULTIPLIER_INPUT_DECIMAL_PLACES);
  if (s.includes('.')) {
    s = s.replace(/0+$/, '').replace(/\.$/, '');
  }
  return s;
}

/**
 * @param {unknown} n
 * @returns {number}
 */
export function roundKrWeightMultiplierInputPercent(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) {
    return 0;
  }
  const m = 10 ** KR_WEIGHT_MULTIPLIER_INPUT_DECIMAL_PLACES;
  return Math.round(x * m) / m;
}

/**
 * Quantize non-negative multipliers to numDecimals fractional digits so their sum matches
 * round(total × 10^d) / 10^d. Extra units apportion round-robin in order of largest fractional remainder
 * (ties broken by smaller metric id). Matches {@link Metric::quantizeKrWeightMultipliersWithRemainderApportion} (PHP).
 *
 * @param {Record<number|string, number>} multiplierById metric id => multiplier
 * @param {number} [numDecimals]
 * @returns {Record<number, number>}
 */
export function quantizeKrWeightMultipliersWithRemainderApportion(
  multiplierById,
  numDecimals = KR_WEIGHT_MULTIPLIER_INPUT_DECIMAL_PLACES,
) {
  const scale = 10 ** numDecimals;
  const ids = Object.keys(multiplierById)
    .map(Number)
    .filter((id) => Number.isFinite(id))
    .sort((a, b) => a - b);
  const values = {};
  for (const id of ids) {
    const m = Number(multiplierById[id]);
    values[id] = Number.isFinite(m) && m >= 0 ? m : 0;
  }
  const sum = ids.reduce((s, id) => s + values[id], 0);
  if (sum <= 0) {
    return Object.fromEntries(ids.map((id) => [id, 0]));
  }
  const targetMicro = Math.round(sum * scale);
  const floorMicro = {};
  const frac = {};
  for (const id of ids) {
    const x = values[id] * scale;
    const f = Math.floor(x + 1e-9);
    floorMicro[id] = f;
    frac[id] = x - f;
  }
  const allocated = ids.reduce((s, id) => s + floorMicro[id], 0);
  let rem = targetMicro - allocated;
  const sorted = [...ids].sort((a, b) => {
    const c = frac[b] - frac[a];
    return c !== 0 ? c : a - b;
  });
  const nSorted = sorted.length;
  if (rem > 0 && nSorted > 0) {
    let idx = 0;
    while (rem > 0) {
      const id = sorted[idx % nSorted];
      floorMicro[id] += 1;
      rem -= 1;
      idx += 1;
    }
  } else if (rem < 0) {
    sorted.sort((a, b) => {
      const c = frac[a] - frac[b];
      return c !== 0 ? c : a - b;
    });
    let needReduce = -rem;
    let idx = 0;
    let guard = 0;
    while (needReduce > 0 && guard < nSorted * 100) {
      const id = sorted[idx % nSorted];
      if (floorMicro[id] > 0) {
        floorMicro[id] -= 1;
        needReduce -= 1;
      }
      idx += 1;
      guard += 1;
    }
  }
  return Object.fromEntries(ids.map((id) => [id, floorMicro[id] / scale]));
}

/**
 * Sum of user-entered share values for KRs that count toward weighted progress.
 *
 * @param {unknown[]} metrics
 * @param {Record<string, { percent?: unknown }>} krWeightage
 * @returns {number}
 */
export function sumEnteredKrWeightPercents(metrics, krWeightage) {
  if (!Array.isArray(metrics) || !krWeightage) {
    return 0;
  }
  return metrics
    .filter((m) => isMetricCountedForKrWeighting(m))
    .reduce((s, m) => {
      const pct = krWeightage[m.id]?.percent;
      if (pct === '' || pct === null || typeof pct === 'undefined') {
        return s;
      }
      const n = parseFloat(String(pct));
      return s + (Number.isFinite(n) ? n : 0);
    }, 0);
}

/**
 * Normalized objective weighting: entered / sum(entered) × 100, to wizard decimal precision.
 *
 * @param {number} enteredShare
 * @param {number} sumEnteredShares
 * @returns {number | null} null when no valid normalized share for this row
 */
export function krNormalizedObjectivePercent(enteredShare, sumEnteredShares) {
  const sum = Number(sumEnteredShares);
  if (!Number.isFinite(sum) || sum <= 0) {
    return null;
  }
  const p = Number(enteredShare);
  if (!Number.isFinite(p) || p < 0) {
    return null;
  }
  return roundKrWeightMultiplierInputPercent((p / sum) * 100);
}

/**
 * Fractional share from stored FY/unscaled weight totals (when multipliers are 0).
 *
 * @param {string|number} weightageRaw
 * @param {number} totalIncludedWeightage
 * @returns {number} share 0–100, {@link KR_WEIGHT_MULTIPLIER_INPUT_DECIMAL_PLACES} fractional digits max
 */
export function krSharePercentFromStoredWeightage(weightageRaw, totalIncludedWeightage) {
  const w = Number(String(weightageRaw).replace(/%$/, '').trim());
  const t = Number(totalIncludedWeightage);
  if (!Number.isFinite(w) || w < 0 || !Number.isFinite(t) || t <= 0) {
    return 0;
  }
  return roundKrWeightMultiplierInputPercent((w / t) * 100);
}

/**
 * @param {unknown[]} metrics goal metrics with progress_consider and weightage
 * @returns {number}
 */
export function sumIncludedMetricsWeightage(metrics) {
  if (!Array.isArray(metrics)) {
    return 0;
  }
  return metrics
    .filter((m) => isMetricCountedForKrWeighting(m))
    .reduce((s, m) => {
      const n = Number(String(m.weightage ?? '').replace(/%$/, '').trim());
      return s + (Number.isFinite(n) && n >= 0 ? n : 0);
    }, 0);
}

/**
 * @param {string|number} weightageRaw stored integer or numeric string (not API-formatted percent)
 * @param {number} totalIncludedWeightage sum of included KRs' stored weightages
 * @returns {number} rounded share of objective 0–100
 */
export function weightageToObjectiveSharePercent(weightageRaw, totalIncludedWeightage) {
  const w = Number(String(weightageRaw).replace(/%$/, '').trim());
  const t = Number(totalIncludedWeightage);
  if (!Number.isFinite(w) || w < 0 || !Number.isFinite(t) || t <= 0) {
    return 0;
  }
  return Math.round((w / t) * 100);
}
